import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, ACTIVITIES_PRIMARY } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { ShieldCheck, Trash2, Pencil, Loader2, Search, BarChart3, Users as UsersIcon, CalendarDays, Sparkles, Activity } from "lucide-react";
import { toast } from "sonner";
import Avatar from "@/components/Avatar";

export default function Admin() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [tab, setTab] = useState("users");
  const [loading, setLoading] = useState(true);
  const [userQ, setUserQ] = useState("");
  const [eventQ, setEventQ] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [filterCat, setFilterCat] = useState("__all");
  const [editing, setEditing] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  useEffect(() => {
    if (user && user.role !== "admin") nav("/discover");
  }, [user, nav]);

  const loadUsers = useCallback(async () => {
    const res = await api.get(`/admin/users${userQ ? `?q=${encodeURIComponent(userQ)}` : ""}`);
    setUsers(res.data);
  }, [userQ]);
  const loadEvents = useCallback(async () => {
    const params = new URLSearchParams();
    params.set("sort", sortBy);
    params.set("order", sortOrder);
    if (eventQ) params.set("q", eventQ);
    if (filterCat && filterCat !== "__all") params.set("category", filterCat);
    const res = await api.get(`/admin/events?${params.toString()}`);
    setEvents(res.data);
  }, [sortBy, sortOrder, eventQ, filterCat]);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await api.get(`/admin/stats?days=14`);
      setStats(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load stats");
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === "reports" && !stats) loadStats();
  }, [tab, stats, loadStats]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadUsers(), loadEvents()]).finally(() => setLoading(false));
  }, [loadUsers, loadEvents]);

  useEffect(() => { loadUsers().catch(() => {}); }, [userQ, loadUsers]);
  useEffect(() => { loadEvents().catch(() => {}); }, [sortBy, sortOrder, filterCat, eventQ, loadEvents]);

  const toggleVerify = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/verify`, { verified: !u.verified });
      toast.success(!u.verified ? "User verified" : "Verification removed");
      loadUsers();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.alias}? This cannot be undone.`)) return;
    try { await api.delete(`/admin/users/${u.id}`); loadUsers(); toast.success("User deleted"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const deleteEvent = async (ev) => {
    if (!window.confirm(`Delete "${ev.title}"?`)) return;
    try { await api.delete(`/admin/events/${ev.id}`); loadEvents(); toast.success("Deleted"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const saveEvent = async () => {
    if (!editing) return;
    setSavingEdit(true);
    try {
      const { id, title, description, category, location, google_rating, price_for_two, price_for_group, best_day, promotion, why_match } = editing;
      await api.put(`/admin/events/${id}`, { title, description, category, location, google_rating, price_for_two, price_for_group, best_day, promotion, why_match });
      toast.success("Saved");
      setEditing(null);
      loadEvents();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setSavingEdit(false); }
  };

  if (!user || user.role !== "admin") return null;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="admin-page">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--secondary))] font-bold">Admin</div>
        <h1 className="mt-1 text-3xl sm:text-4xl font-heading font-semibold tracking-tight">Admin console</h1>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="rounded-full bg-white border border-[hsl(var(--border))] p-1">
          <TabsTrigger value="users" className="rounded-full px-5" data-testid="admin-tab-users">Users ({users.length})</TabsTrigger>
          <TabsTrigger value="events" className="rounded-full px-5" data-testid="admin-tab-events">Events ({events.length})</TabsTrigger>
          <TabsTrigger value="reports" className="rounded-full px-5" data-testid="admin-tab-reports">
            <BarChart3 className="h-3.5 w-3.5 me-1.5" /> Reports
          </TabsTrigger>
        </TabsList>

        {/* Users */}
        <TabsContent value="users" className="mt-6">
          <div className="mb-4 flex items-center gap-2">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute start-3 top-2.5 h-4 w-4 text-[hsl(var(--muted-foreground))]" />
              <Input value={userQ} onChange={(e) => setUserQ(e.target.value)} placeholder="Search alias…" className="ps-9 rounded-full" data-testid="admin-user-search" />
            </div>
          </div>
          <div className="bg-white border border-[hsl(var(--border))] rounded-3xl overflow-hidden divide-y divide-[hsl(var(--border))]">
            {loading ? <div className="p-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--primary))]" /></div> : users.map((u) => (
              <div key={u.id} className="flex items-center gap-3 p-4" data-testid={`admin-user-${u.id}`}>
                <Avatar user={u} size={40} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{u.alias}</span>
                    {u.role === "admin" && <span className="text-[10px] uppercase font-bold bg-[hsl(var(--secondary))] text-white px-2 py-0.5 rounded-full">admin</span>}
                    {u.verified && <span className="text-[10px] uppercase font-bold bg-[hsl(var(--primary))] text-white px-2 py-0.5 rounded-full">verified</span>}
                  </div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))]">{u.city || "—"} · {(u.languages_spoken || []).join(", ")}</div>
                </div>
                <Button size="sm" variant={u.verified ? "outline" : "default"} onClick={() => toggleVerify(u)} data-testid={`admin-verify-${u.id}`} className={u.verified ? "rounded-full border-[hsl(var(--border))]" : "rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-hover))] text-white"}>
                  <ShieldCheck className="h-3.5 w-3.5 me-1" /> {u.verified ? "Unverify" : "Verify"}
                </Button>
                {u.id !== user.id && (
                  <Button size="icon" variant="ghost" onClick={() => deleteUser(u)} data-testid={`admin-del-user-${u.id}`} className="text-[hsl(var(--destructive))] rounded-full">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Events */}
        <TabsContent value="events" className="mt-6">
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute start-3 top-2.5 h-4 w-4 text-[hsl(var(--muted-foreground))]" />
              <Input value={eventQ} onChange={(e) => setEventQ(e.target.value)} placeholder="Search title…" className="ps-9 rounded-full" data-testid="admin-event-search" />
            </div>
            <Select value={filterCat} onValueChange={setFilterCat}>
              <SelectTrigger className="w-[180px] rounded-full" data-testid="admin-event-cat"><SelectValue placeholder="All categories" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All categories</SelectItem>
                {ACTIVITIES_PRIMARY.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[160px] rounded-full" data-testid="admin-event-sort"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="created_at">Date created</SelectItem>
                <SelectItem value="title">Title</SelectItem>
                <SelectItem value="category">Category</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sortOrder} onValueChange={setSortOrder}>
              <SelectTrigger className="w-[130px] rounded-full" data-testid="admin-event-order"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="desc">Newest first</SelectItem>
                <SelectItem value="asc">Oldest first</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="bg-white border border-[hsl(var(--border))] rounded-3xl overflow-hidden divide-y divide-[hsl(var(--border))]">
            {loading ? <div className="p-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--primary))]" /></div> : events.length === 0 ? (
              <div className="p-8 text-center text-sm text-[hsl(var(--muted-foreground))]">No events match.</div>
            ) : events.map((ev) => (
              <div key={ev.id} className="flex items-start gap-3 p-4" data-testid={`admin-event-${ev.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{ev.title}</span>
                    <span className="text-[10px] uppercase font-bold bg-[hsl(var(--elevated))] border border-[hsl(var(--border))] px-2 py-0.5 rounded-full">{ev.category}</span>
                  </div>
                  <div className="mt-0.5 text-xs text-[hsl(var(--muted-foreground))] truncate">{ev.location || ev.description?.slice(0, 80)}</div>
                  <div className="mt-0.5 text-[10px] text-[hsl(var(--muted-foreground))]">{new Date(ev.created_at).toLocaleString()}</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => setEditing(ev)} data-testid={`admin-edit-event-${ev.id}`} className="rounded-full border-[hsl(var(--border))]">
                  <Pencil className="h-3.5 w-3.5 me-1" /> Edit
                </Button>
                <Button size="icon" variant="ghost" onClick={() => deleteEvent(ev)} data-testid={`admin-del-event-${ev.id}`} className="text-[hsl(var(--destructive))] rounded-full">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Reports */}
        <TabsContent value="reports" className="mt-6">
          {statsLoading || !stats ? (
            <div className="bg-white border border-[hsl(var(--border))] rounded-3xl p-12 flex justify-center" data-testid="admin-reports-loading">
              <Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--primary))]" />
            </div>
          ) : (
            <div className="space-y-6" data-testid="admin-reports">
              {/* KPI tiles */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                <StatTile icon={<UsersIcon className="h-4 w-4" />} label="Users" value={stats.totals.users} sub={`${stats.totals.verified_users} verified`} testid="stat-users" />
                <StatTile icon={<Activity className="h-4 w-4" />} label="DAU" value={stats.active_users.dau_24h} sub="last 24h" testid="stat-dau" />
                <StatTile icon={<Activity className="h-4 w-4" />} label="WAU" value={stats.active_users.wau_7d} sub="last 7d" testid="stat-wau" />
                <StatTile icon={<Sparkles className="h-4 w-4" />} label="Groups" value={stats.totals.groups} sub={`max 10/group`} testid="stat-groups" />
                <StatTile icon={<CalendarDays className="h-4 w-4" />} label="AI events" value={stats.totals.ai_events} sub="curated ideas" testid="stat-ai-events" />
                <StatTile icon={<CalendarDays className="h-4 w-4" />} label="Scheduled" value={stats.totals.scheduled_events} sub={`${stats.totals.group_events} group events`} testid="stat-scheduled" />
              </div>

              {/* Time series */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <BarSeriesCard title="New users (last 14d)" data={stats.registrations_per_day} testid="chart-registrations" />
                <BarSeriesCard title="Groups created (last 14d)" data={stats.groups_per_day} testid="chart-groups" />
              </div>

              {/* RSVP funnel */}
              <div className="bg-white border border-[hsl(var(--border))] rounded-3xl p-5" data-testid="chart-rsvp-funnel">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))] font-bold">RSVP funnel</div>
                    <div className="text-lg font-heading font-semibold mt-0.5">Across {stats.totals.group_events} group events</div>
                  </div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))]">
                    {stats.rsvp_funnel.total_invited} invitations
                  </div>
                </div>
                <RsvpFunnel funnel={stats.rsvp_funnel} />
              </div>

              <div className="flex justify-end">
                <Button variant="outline" onClick={() => { setStats(null); loadStats(); }} className="rounded-full border-[hsl(var(--border))]" data-testid="admin-refresh-stats">
                  Refresh
                </Button>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="rounded-3xl max-w-lg" data-testid="admin-edit-dialog">
          <DialogHeader><DialogTitle>Edit event</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              <div><Label>Title</Label><Input value={editing.title || ""} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="mt-1.5 rounded-xl" /></div>
              <div><Label>Category</Label>
                <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                  <SelectTrigger className="mt-1.5 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>{ACTIVITIES_PRIMARY.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Description</Label><Textarea value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="mt-1.5 rounded-xl" rows={2} /></div>
              <div><Label>Location</Label><Input value={editing.location || ""} onChange={(e) => setEditing({ ...editing, location: e.target.value })} className="mt-1.5 rounded-xl" /></div>
              <div><Label>Google rating</Label><Input value={editing.google_rating || ""} onChange={(e) => setEditing({ ...editing, google_rating: e.target.value })} className="mt-1.5 rounded-xl" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Price for 2</Label><Input value={editing.price_for_two || ""} onChange={(e) => setEditing({ ...editing, price_for_two: e.target.value })} className="mt-1.5 rounded-xl" /></div>
                <div><Label>Price for group</Label><Input value={editing.price_for_group || ""} onChange={(e) => setEditing({ ...editing, price_for_group: e.target.value })} className="mt-1.5 rounded-xl" /></div>
              </div>
              <div><Label>Best day</Label><Input value={editing.best_day || ""} onChange={(e) => setEditing({ ...editing, best_day: e.target.value })} className="mt-1.5 rounded-xl" /></div>
              <div><Label>Promotion</Label><Input value={editing.promotion || ""} onChange={(e) => setEditing({ ...editing, promotion: e.target.value })} className="mt-1.5 rounded-xl" /></div>
              <div><Label>Why it fits</Label><Textarea value={editing.why_match || ""} onChange={(e) => setEditing({ ...editing, why_match: e.target.value })} className="mt-1.5 rounded-xl" rows={2} /></div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={saveEvent} disabled={savingEdit} data-testid="admin-save-event" className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-hover))] text-white">
              {savingEdit ? <Loader2 className="h-4 w-4 animate-spin me-1" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function StatTile({ icon, label, value, sub, testid }) {
  return (
    <div className="bg-white border border-[hsl(var(--border))] rounded-2xl p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-[hsl(var(--muted-foreground))]">
        <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-[hsl(var(--elevated))] text-[hsl(var(--primary))]">{icon}</span>
        <span className="text-[11px] uppercase tracking-[0.18em] font-bold">{label}</span>
      </div>
      <div className="mt-2 text-3xl font-heading font-semibold tracking-tight">{value}</div>
      <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">{sub}</div>
    </div>
  );
}

function BarSeriesCard({ title, data, testid }) {
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <div className="bg-white border border-[hsl(var(--border))] rounded-3xl p-5" data-testid={testid}>
      <div className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))] font-bold">{title}</div>
      <div className="mt-4 flex items-end gap-1 h-32">
        {data.map((d) => {
          const h = Math.round((d.count / max) * 100);
          return (
            <div key={d.date} className="flex-1 flex flex-col items-center group" title={`${d.date}: ${d.count}`}>
              <div
                className="w-full rounded-t-md bg-[hsl(var(--primary))] group-hover:bg-[hsl(var(--primary-hover))] transition-all"
                style={{ height: `${Math.max(2, h)}%` }}
              />
            </div>
          );
        })}
      </div>
      <div className="mt-2 flex justify-between text-[10px] text-[hsl(var(--muted-foreground))]">
        <span>{data[0]?.date.slice(5)}</span>
        <span>{data[data.length - 1]?.date.slice(5)}</span>
      </div>
    </div>
  );
}

function RsvpFunnel({ funnel }) {
  const items = [
    { key: "accepted", label: "Accepted", color: "hsl(var(--primary))" },
    { key: "maybe", label: "Maybe", color: "hsl(45 95% 55%)" },
    { key: "declined", label: "Declined", color: "hsl(var(--destructive))" },
    { key: "pending", label: "Pending", color: "hsl(var(--muted-foreground))" },
  ];
  const total = Math.max(1, items.reduce((s, it) => s + (funnel[it.key] || 0), 0));
  return (
    <div className="space-y-3">
      <div className="flex h-3 rounded-full overflow-hidden bg-[hsl(var(--elevated))]">
        {items.map((it) => {
          const pct = ((funnel[it.key] || 0) / total) * 100;
          if (pct <= 0) return null;
          return <div key={it.key} style={{ width: `${pct}%`, background: it.color }} />;
        })}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {items.map((it) => (
          <div key={it.key} className="flex items-center gap-2 text-sm" data-testid={`rsvp-${it.key}`}>
            <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: it.color }} />
            <span className="text-[hsl(var(--muted-foreground))]">{it.label}</span>
            <span className="font-semibold ms-auto">{funnel[it.key] || 0}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
